import React from 'react';

import { formatDate, formatNombre } from '../../lib/formats';
import { cn, formatCurrency, formatVolume } from '../../lib/utils';
import { StatutBadge } from '../ui/StatutBadge';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';

const nombre = (valeur) => formatNombre(valeur, 0, '—');

const Chiffre = ({ valeur, gras = false, className }) => (
  <TableCell
    className={cn(
      'whitespace-nowrap text-right tabular-nums',
      Number(valeur) < 0 && 'text-destructive',
      gras && 'font-semibold',
      className,
    )}
  >
    {nombre(valeur)}
  </TableCell>
);

/**
 * Les lignes d'un état validé ou annulé, comme sur l'état imprimé:
 * DATE | FACTURE | TRACTEUR | CITERNE | QUANTITE | PRIX | MONTANT | MANQUANT |
 * RETENU | DEPENSE | SOLDE, et la ligne de totaux.
 */
export const LignesEtat = ({ lignes, totaux }) => (
  <section className="space-y-2" aria-label="Trajets de l’état" data-testid="etat-lignes-lecture">
    <h2 className="text-sm font-bold text-foreground">Trajets</h2>
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <Table stickyFirstColumn={false}>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Facture</TableHead>
            <TableHead>Tracteur</TableHead>
            <TableHead>Citerne</TableHead>
            <TableHead className="text-right">Quantité</TableHead>
            <TableHead className="text-right">Prix</TableHead>
            <TableHead className="text-right">Montant</TableHead>
            <TableHead className="text-right">Manquant</TableHead>
            <TableHead className="text-right">Retenue</TableHead>
            <TableHead className="text-right">Dépense</TableHead>
            <TableHead className="text-right">Solde</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lignes.map((ligne) => (
            <TableRow key={ligne.id || ligne.trip_id} data-testid={`etat-ligne-lecture-${ligne.trip_id}`}>
              <TableCell className="whitespace-nowrap tabular-nums">{formatDate(ligne.line_date)}</TableCell>
              <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums">
                {ligne.invoice_line_number ?? '—'}
              </TableCell>
              <TableCell className="whitespace-nowrap font-semibold">{ligne.tractor_plate || '—'}</TableCell>
              <TableCell className="whitespace-nowrap">{ligne.tank_plate || '—'}</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(ligne.quantity)}</TableCell>
              <Chiffre valeur={ligne.price} />
              <Chiffre valeur={ligne.amount} />
              <TableCell className="whitespace-nowrap text-right tabular-nums">
                {ligne.shortage_known ? formatVolume(ligne.shortage_liters) : '—'}
              </TableCell>
              <Chiffre valeur={ligne.retention} />
              <Chiffre valeur={ligne.expenses} />
              <Chiffre valeur={ligne.balance} gras />
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow className="font-bold" data-testid="etat-totaux-lecture">
            <TableCell colSpan={4}>Total</TableCell>
            <TableCell className="whitespace-nowrap text-right tabular-nums">{formatVolume(totaux.quantite)}</TableCell>
            <TableCell />
            <Chiffre valeur={totaux.montant} />
            <TableCell />
            <Chiffre valeur={totaux.retenue} />
            <Chiffre valeur={totaux.depense} />
            <Chiffre valeur={totaux.solde} />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  </section>
);

const Ligne = ({ libelle, valeur, signe = '', gras = false, testid }) => (
  <div className={cn('flex items-baseline justify-between gap-6', gras && 'font-semibold')}>
    <dt className="text-sm text-muted-foreground">{libelle}</dt>
    <dd className="whitespace-nowrap text-sm tabular-nums" data-testid={testid}>
      {signe}
      {formatCurrency(valeur)}
    </dd>
  </div>
);

/**
 * Le bas de l'état: Σ soldes − autres dépenses − avances = net à payer.
 * Pour un état validé, le paiement se lit sur sa facture fournisseur
 * (`bill`): payé, payé en partie, ou à payer avec ce qui reste dû.
 */
export const RecapEtat = ({ totaux, bill = null, apercu = false }) => (
  <section
    className="ml-auto w-full max-w-sm space-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3"
    aria-label="Récapitulatif de l’état"
    data-testid="etat-recap"
  >
    <dl className="space-y-1">
      <Ligne libelle="Total des soldes" valeur={totaux.solde} testid="recap-soldes" />
      {totaux.autresDepenses ? (
        <Ligne libelle="Autres dépenses du camion" valeur={totaux.autresDepenses} signe="− " testid="recap-depenses" />
      ) : null}
      {totaux.avances ? <Ligne libelle="Avances" valeur={totaux.avances} signe="− " testid="recap-avances" /> : null}
    </dl>
    <div className="flex items-baseline justify-between gap-6 border-t border-border pt-2">
      <p className="text-sm font-semibold">Net à payer</p>
      <p className={cn('kpi-value whitespace-nowrap tabular-nums', apercu && 'opacity-80')} data-testid="recap-net">
        {formatCurrency(totaux.net)}
      </p>
    </div>
    {bill && bill.payment_status ? (
      <div className="flex items-center justify-between gap-3 border-t border-border pt-2" data-testid="recap-paiement">
        <StatutBadge domaine="statut_paiement_etat" code={bill.payment_status} />
        {bill.payment_status !== 'PAYE' ? (
          <span className="whitespace-nowrap text-sm tabular-nums">Reste {formatCurrency(bill.remaining)}</span>
        ) : null}
      </div>
    ) : null}
  </section>
);
